import { useEffect } from "react";

import { reaction } from "mobx";

import { addMiddleware } from "src/api/requestMiddleware";
import { hasSharedSession, onCrossTabLogout } from "src/api/tokenStorage";
import { useAuthoringUIActions } from "src/flowContainer/AuthoringUIContext";
import { getSignUpUrlWithNext } from "src/router/urls";
import { useAuthStore } from "src/store/AuthStore";
import { GraphStore } from "src/store/GraphStore";

export const graphStore = new GraphStore();

addMiddleware();

const tearDownSession = () => {
  useAuthStore.getState().actions.resetAll();
  window.location.assign(getSignUpUrlWithNext());
};

// Cross-tab logout: this tab's live tokens live in sessionStorage, so they
// survive reload/discard and can shadow a seed another tab already cleared.
// A tab's session is only valid while the shared localStorage seed exists, so
// reconcile against it at every point the tab comes to life:
const loggedOutElsewhere =
  !!useAuthStore.getState().refresh_token && !hasSharedSession();
if (loggedOutElsewhere) {
  tearDownSession();
} else {
  useAuthStore.getState().actions.setup();
}

onCrossTabLogout(tearDownSession);

document.addEventListener("visibilitychange", () => {
  const stillSignedInHere = !!useAuthStore.getState().refresh_token;
  if (
    document.visibilityState === "visible" &&
    stillSignedInHere &&
    !hasSharedSession()
  ) {
    tearDownSession();
  }
});

export const useGraphStore = () => {
  return graphStore;
};

/**
 * We have some new state items in AuthoringUIContext now
 * so we need to sync the graph store with the AuthoringUIContext store sometimes,
 * here we can react on the graph store changes and update the store
 */
export const useSyncWithGraphStore = () => {
  const { setNodeEditorOpen } = useAuthoringUIActions();

  useEffect(() => {
    // When selectedNode has changed and set to a new value
    // we need to open the node editor
    reaction(
      () => graphStore.selectedNode?.id,
      (value, previousValue) => {
        if (value && value !== previousValue) {
          setNodeEditorOpen(true);
        }
      },
    );
  }, [setNodeEditorOpen]);
};
